import { Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import { FieldHeader } from '../common/FieldHeader';
import { IntegerDisplay } from './IntegerDisplay';
import { IntegerEmptyState } from './IntegerEmptyState';
import { IntegerUpdateModal } from './IntegerUpdateModal';
import { IntegerDiffModal } from './IntegerDiffModal';
import { FormFieldIntegerInput} from '@publishing-engine/domain/types/article';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

export type IFormFieldInteger = FormFieldIntegerInput;

type IProps = {
  formFieldDefinition: IFormFieldInteger;
  value: number;
  previousValue?: number | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: number) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?:boolean;
};

export const GenericIntegerField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <IntegerUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: number) => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <IntegerDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {typeof value === 'number' ? (
        <IntegerDisplay value={value} />
      ) : (
        <IntegerEmptyState />
      )}
    </Flex>
  );
};
